import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowRight, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { getDashboardUrl } from '../../lib/domains';

export default function Login() {
  const { login, verify2FA, verifyEmailOtp, resendEmailOtp, verifyLoginOtp, resendLoginOtp } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [twoFaState, setTwoFaState] = useState(null);
  const [twoFaCode, setTwoFaCode] = useState('');
  const [loginOtpState, setLoginOtpState] = useState(null);
  const [loginOtpCode, setLoginOtpCode] = useState('');
  const [emailOtpState, setEmailOtpState] = useState(null);
  const [emailOtpCode, setEmailOtpCode] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setInfo('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate('/dashboard');
    } else if (result.requires_login_otp) {
      setEmailOtpState(null);
      setTwoFaState(null);
      setLoginOtpState({ tempToken: result.temp_token, email: result.email });
      setInfo(result.message || '');
      setError('');
    } else if (result.requires_email_otp) {
      setLoginOtpState(null);
      setTwoFaState(null);
      setEmailOtpState({ tempToken: result.temp_token, email: result.email });
      setInfo(result.message || '');
      setError('');
    } else if (result.requires_2fa) {
      setLoginOtpState(null);
      setEmailOtpState(null);
      setTwoFaState({ tempToken: result.temp_token });
      setError('');
    } else {
      setError(result.error);
    }
  };

  const handle2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await verify2FA(twoFaState.tempToken, twoFaCode);
    setLoading(false);
    if (result.success) navigate('/dashboard');
    else setError(result.error);
  };

  const handleEmailOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await verifyEmailOtp(emailOtpState.tempToken, emailOtpCode);
    setLoading(false);
    if (result.success) navigate('/dashboard');
    else setError(result.error);
  };

  const handleLoginOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await verifyLoginOtp(loginOtpState.tempToken, loginOtpCode);
    setLoading(false);
    if (result.success) navigate('/dashboard');
    else setError(result.error);
  };

  const handleResendOtp = async () => {
    setError(''); setInfo('');
    const r = await resendEmailOtp(emailOtpState.tempToken);
    if (r.success) setInfo('A new code has been sent to your email.');
    else setError(r.error);
  };

  const handleResendLoginOtp = async () => {
    setError(''); setInfo('');
    const r = await resendLoginOtp(loginOtpState.tempToken);
    if (r.success) setInfo('A new code has been sent to your email.');
    else setError(r.error);
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Left side - branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-emerald-700 text-white flex-col justify-between p-12">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Jjuma Pay</h1>
          <p className="text-emerald-200 mt-1 text-sm">Payment Gateway for East Africa</p>
        </div>
        <div>
          <h2 className="text-2xl font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Receive payments from anywhere
          </h2>
          <p className="text-emerald-100 leading-relaxed max-w-md">
            Create payment links, receive money from supporters and customers, manage your transactions,
            and grow your business with Jjuma Pay.
          </p>
        </div>
        <p className="text-emerald-300 text-xs">Trusted by creators and businesses across East Africa</p>
      </div>

      {/* Right side - form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <h1 className="text-2xl font-bold text-emerald-700" style={{ fontFamily: 'Outfit, sans-serif' }}>Jjuma Pay</h1>
          </div>
          <h2 className="text-2xl font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Welcome back
          </h2>
          <p className="text-slate-500 text-sm mb-6">Sign in to your account</p>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="login-error">
              {error}
            </div>
          )}
          {info && !error && (
            <div className="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm" data-testid="login-info">
              {info}
            </div>
          )}

          {emailOtpState ? (
            <div>
              <div className="mb-4 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-center">
                <ShieldCheck size={24} className="mx-auto text-emerald-600 mb-2" />
                <p className="text-sm text-emerald-800 font-medium">Verify your email to continue</p>
                <p className="text-xs text-emerald-600 mt-1">A code was sent to <span className="font-mono">{emailOtpState.email}</span></p>
              </div>
              <form onSubmit={handleEmailOtp} className="space-y-4">
                <div>
                  <Label htmlFor="email-otp-code" className="text-slate-700">Verification Code</Label>
                  <Input id="email-otp-code" data-testid="email-otp-input" value={emailOtpCode} onChange={e => setEmailOtpCode(e.target.value)} placeholder="Enter 6-digit code" className="mt-1.5 text-center text-lg tracking-widest" maxLength={6} required autoFocus />
                </div>
                <Button type="submit" data-testid="email-otp-submit-btn" disabled={loading} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white">
                  {loading ? 'Verifying…' : 'Verify & Continue'}
                </Button>
                <div className="flex justify-between text-sm">
                  <button type="button" onClick={handleResendOtp} className="text-emerald-700 hover:underline" data-testid="email-otp-resend-btn">
                    Resend code
                  </button>
                  <button type="button" onClick={() => { setEmailOtpState(null); setEmailOtpCode(''); setInfo(''); }} className="text-slate-500 hover:text-slate-700" data-testid="email-otp-cancel-btn">
                    Back to login
                  </button>
                </div>
              </form>
            </div>
          ) : loginOtpState ? (
            <div>
              <div className="mb-4 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-center">
                <ShieldCheck size={24} className="mx-auto text-emerald-600 mb-2" />
                <p className="text-sm text-emerald-800 font-medium">Verify your login</p>
                <p className="text-xs text-emerald-600 mt-1">A code was sent to <span className="font-mono">{loginOtpState.email}</span></p>
              </div>
              <form onSubmit={handleLoginOtp} className="space-y-4">
                <div>
                  <Label htmlFor="login-otp-code" className="text-slate-700">Verification Code</Label>
                  <Input id="login-otp-code" data-testid="login-otp-input" value={loginOtpCode} onChange={e => setLoginOtpCode(e.target.value)} placeholder="Enter 6-digit code" className="mt-1.5 text-center text-lg tracking-widest" maxLength={6} required autoFocus />
                </div>
                <Button type="submit" data-testid="login-otp-submit-btn" disabled={loading} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white">
                  {loading ? 'Verifying…' : 'Verify & Continue'}
                </Button>
                <div className="flex justify-between text-sm">
                  <button type="button" onClick={handleResendLoginOtp} className="text-emerald-700 hover:underline" data-testid="login-otp-resend-btn">
                    Resend code
                  </button>
                  <button type="button" onClick={() => { setLoginOtpState(null); setLoginOtpCode(''); setInfo(''); }} className="text-slate-500 hover:text-slate-700" data-testid="login-otp-cancel-btn">
                    Back to login
                  </button>
                </div>
              </form>
            </div>
          ) : twoFaState ? (
            <div>
              <div className="mb-4 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-center">
                <ShieldCheck size={24} className="mx-auto text-emerald-600 mb-2" />
                <p className="text-sm text-emerald-800 font-medium">Verification code sent to your email</p>
                <p className="text-xs text-emerald-600 mt-1">Check your inbox for the 6-digit code</p>
              </div>
              <form onSubmit={handle2FA} className="space-y-4">
                <div>
                  <Label htmlFor="code" className="text-slate-700">Verification Code</Label>
                  <Input id="code" data-testid="2fa-code-input" value={twoFaCode} onChange={e => setTwoFaCode(e.target.value)} placeholder="Enter 6-digit code" className="mt-1.5 text-center text-lg tracking-widest" maxLength={6} required autoFocus />
                </div>
                <Button type="submit" data-testid="2fa-submit-btn" disabled={loading} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white">
                  {loading ? 'Verifying...' : 'Verify & Sign In'}
                </Button>
                <button type="button" onClick={() => { setTwoFaState(null); setTwoFaCode(''); }} className="w-full text-sm text-slate-500 hover:text-slate-700" data-testid="2fa-cancel-btn">
                  Back to login
                </button>
              </form>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1.5"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPw ? 'text' : 'password'}
                  data-testid="login-password-input"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
            >
              {loading ? 'Signing in...' : 'Sign In'}
              {!loading && <ArrowRight size={16} className="ml-2" />}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-slate-500 hover:text-emerald-700">
              Forgot your password?
            </Link>
          </div>

          <p className="mt-4 text-center text-sm text-slate-500">
            Don't have an account?{' '}
            <Link to={getDashboardUrl('/register')} data-testid="register-link" className="text-emerald-700 font-medium hover:underline">
              Create account
            </Link>
          </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
